import clsx from 'clsx';
import type { Ref } from 'react';
import React, { forwardRef, Fragment, useCallback, useImperativeHandle, useLayoutEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import type { Observable } from 'rxjs';
import useTestTag from '../../common/hooks/use-test-tag';
import Button from '../button/button';
import type { ModalDialogButtonSetting } from '../common-interfaces';
import OdinIcon, { OdinIconSize } from '../odin-icon/odin-icon.component';

interface DialogResult {
  buttonName: string | undefined;
}

export interface DialogRef {
  show: () => void;
  close: () => void;
  subscribeToDialogResult?: () => Observable<DialogResult> | undefined;
}

export interface DialogProps extends React.PropsWithChildren {
  buttons?: Array<ModalDialogButtonSetting>;
  buttonType?: 'button' | 'submit';
  containerClassName?: string;
  contentClassName?: string;
  footer?: string | JSX.Element;
  fullScreen?: boolean;
  header?: string | JSX.Element;
  id?: string;
  loading?: boolean;
  showCloseIcon?: boolean;
  visible?: boolean;
  onClose?: (returnValue?: string) => void;
  /**
   * Controls whether to use the native HTML `<dialog>` element or a custom portal-based implementation.
   *
   * @default true
   *
   * **When to use `true` (native dialog - default):**
   * - Standard modal dialogs with typical behavior
   * - Better accessibility (native focus trap, ESC key handling)
   * - Better performance (no React portal overhead)
   * - Built-in backdrop click handling
   *
   * **When to use `false` (custom portal implementation):**
   * - **When wrapping Dialog with OdinTransition or Framer Motion** - Native `<dialog>` elements conflict with
   *   Framer Motion's animation requirements because they render in the browser's top layer, preventing proper
   *   animation control. Use `useNativeDialog={false}` to enable smooth enter/exit transitions.
   * - When you need more control over the backdrop or transition behavior
   * - When compatibility with older browsers that don't support `<dialog>` is required
   * - When the native dialog constraints (e.g., top layer stacking) cause other integration issues
   *
   * **Key differences:**
   * - Native: Uses `<dialog>` element with `showModal()`, renders in browser's top layer
   * - Custom: Uses React portal with manual backdrop and positioning, renders in document.body
   */
  useNativeDialog?: boolean;
}

function Dialog(
  {
    buttons,
    buttonType = 'button',
    children,
    containerClassName,
    contentClassName,
    footer,
    fullScreen = false,
    header,
    id,
    loading = false,
    showCloseIcon = true,
    visible = false,
    onClose,
    useNativeDialog = true,
  }: DialogProps,
  ref: Ref<DialogRef>
) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const tagCreator = useTestTag('modal');
  const onCloseCalledRef = useRef(false);

  // Sync native dialog open state with visible prop
  useLayoutEffect(() => {
    if (!useNativeDialog) return;
    const dialog = dialogRef.current;
    if (!dialog) return;

    // Only make DOM calls when current state doesn't match target state.
    // This prevents race conditions from rapid visible prop changes because
    // we check the actual dialog.open state right before each DOM call.
    if (visible && !dialog.open) {
      dialog.showModal();
      // Reset the flag only during open transition (closed → open).
      // This allows onClose to be called when the dialog actually closes.
      onCloseCalledRef.current = false;
    } else if (!visible && dialog.open) {
      dialog.close();
    }
  }, [visible, useNativeDialog]);

  const show = () => {
    if (!useNativeDialog) {
      return;
    }
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) {
      dialog.showModal();
    }
  };

  const close = (returnValue?: string) => {
    if (!useNativeDialog) {
      onClose?.(returnValue);
      return;
    }

    const dialog = dialogRef.current;
    if (dialog?.open) {
      // Set returnValue before calling close() to ensure it's captured correctly
      if (returnValue) {
        dialog.returnValue = returnValue;
      }
      dialog.close();
      // Call onClose immediately to ensure parent state is updated without waiting for the native 'close' event.
      // Set the flag AFTER calling onClose to prevent handleNativeClose from calling it again.
      // This handles cases where dialog.close() triggers the native 'close' event synchronously.
      onClose?.(returnValue);
      onCloseCalledRef.current = true;
    } else {
      // If already closed, just notify parent (only if not already called)
      if (!onCloseCalledRef.current) {
        onCloseCalledRef.current = true;
        onClose?.(returnValue);
      }
    }
  };

  useImperativeHandle(ref, () => ({
    show,
    close,
  }));

  const onDialogClick = (event: React.MouseEvent<HTMLDialogElement>) => {
    if (event.target === dialogRef.current) {
      close('overlay');
    }
  };

  const handleNativeClose = useCallback(() => {
    // This fires when the dialog is closed by ANY means (ESC, form method="dialog", dialog.close())
    // Only call onClose if it hasn't been called already (e.g., from the close() method)
    // This prevents duplicate calls that could trigger side effects twice.
    if (!onCloseCalledRef.current) {
      onCloseCalledRef.current = true;
      const returnValue = dialogRef.current?.returnValue;
      onClose?.(returnValue);
    }
  }, [onClose]);

  const renderHeader = useCallback(() => {
    return (
      <div className="odin-flex odin-shrink-0 odin-items-center odin-gap-2 odin-pb-2">
        <div className="odin-grow odin-truncate odin-text-base odin-font-medium">{header}</div>
        {showCloseIcon && (
          <div
            className="odin-grid odin-h-full odin-place-content-start odin-cursor-pointer odin-text-primary-2"
            onClick={() => close('close-icon')}
            ref={tagCreator('close-icon')}
          >
            <OdinIcon icon="X" />
          </div>
        )}
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- close and tagCreator are stable functions
  }, [header, showCloseIcon]);

  const renderFooterButton = useCallback(
    (button: ModalDialogButtonSetting) => (
      <div className="odin-flex-auto" ref={tagCreator(`${button.text?.replace(/\s+/g, '-').toLowerCase()}-button`)}>
        <Button
          htmlType={buttonType}
          classNames={button.classNames}
          type={button.type}
          variant={button.variant}
          form={button.form}
          disabled={button.disabled}
          name={button.name}
          onClick={() => {
            button.onClick?.();
            if (button.closeModalOnClick) {
              close(button.name);
            }
          }}
        >
          {button.text}
        </Button>
      </div>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- close and tagCreator are stable functions
    [buttonType]
  );

  const renderFooter = useCallback(() => {
    if (buttons || footer) {
      let renderedButtons: JSX.Element = <></>;
      let leftAlignedButtons: ModalDialogButtonSetting[] = [];

      if (buttons) {
        leftAlignedButtons = buttons.filter((button) => button.alignment === 'left');
        const rightAlignedButtons = buttons.filter((button) => button.alignment !== 'left');

        const buttonsWrapperClasses = clsx('odin-flex odin-items-center odin-justify-between', {
          'odin-w-full': leftAlignedButtons.length > 0 || !footer,
        });

        renderedButtons = (
          <div className={buttonsWrapperClasses}>
            <div className="odin-flex odin-items-center odin-justify-center odin-gap-x-2 odin-self-start">
              {leftAlignedButtons.map((button) => (
                <Fragment key={button.name}>{renderFooterButton(button)}</Fragment>
              ))}
            </div>
            <div className="odin-flex odin-items-center odin-justify-center odin-gap-x-2 odin-self-end">
              {rightAlignedButtons.map((button) => (
                <Fragment key={button.name}>{renderFooterButton(button)}</Fragment>
              ))}
            </div>
          </div>
        );
      }

      const footerClasses = clsx(
        'odin-truncate odin-p-1',
        { 'odin-grow': leftAlignedButtons.length > 0 },
        { 'odin-flex-1': leftAlignedButtons.length === 0 }
      );

      return (
        <div className="odin-flex odin-shrink-0 odin-border-t odin-border-solid odin-border-primary-5 odin-pt-4">
          {footer && <div className={footerClasses}>{footer}</div>}
          {renderedButtons}
        </div>
      );
    }
  }, [buttons, footer, renderFooterButton]);

  const containerStyles = clsx(
    'odin-flex odin-flex-col odin-p-4 odin-max-h-[calc(100svh-2rem)]',
    {
      'odin-w-[calc(100svw-2rem)] odin-h-[calc(100svh-2rem)]': fullScreen,
    },
    containerClassName
  );

  const contentStyles = clsx('odin-flex-1 odin-overflow-auto odin-min-h-0 odin-py-4 custom-scroll', contentClassName);

  const baseDialogStyles = 'odin-m-auto odin-rounded-md odin-outline-none odin-overflow-hidden odin-text-gray-1';

  const dialogStyles = clsx(baseDialogStyles, {
    '[&:not([open])]:pointer-events-none': useNativeDialog, // Only applies to actual <dialog> elements
  });

  if (!useNativeDialog) {
    if (!visible) {
      return null;
    }

    return ReactDOM.createPortal(
      <div className="odin-fixed odin-inset-0 odin-z-[1000]">
        <div
          className="odin-absolute odin-inset-0 odin-bg-[rgba(0,0,0,0.5)] odin-z-0"
          onClick={() => close('overlay')}
        />
        <div className="odin-relative odin-flex odin-h-full odin-w-full odin-items-center odin-justify-center odin-z-10">
          {loading && (
            <div className="odin-absolute odin-inset-0 odin-z-20 odin-bg-[rgba(247,247,247,0.4)] odin-grid odin-place-content-center">
              <OdinIcon icon="Loader" size={OdinIconSize.Medium} spin />
            </div>
          )}
          <div className={clsx(baseDialogStyles, 'odin-relative odin-z-10')} role="dialog" aria-modal="true" id={id}>
            <div className={containerStyles} ref={tagCreator('container')}>
              {renderHeader()}
              <div className={contentStyles} ref={tagCreator('content')}>
                {children}
              </div>
              {renderFooter()}
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  const createDialog = () => (
    <dialog ref={dialogRef} id={id} className={dialogStyles} onClick={onDialogClick} onClose={handleNativeClose}>
      {loading && (
        <div className="odin-absolute odin-w-full odin-h-full odin-z-10 odin-bg-[rgba(247,247,247,0.4)] odin-grid odin-place-content-center">
          <OdinIcon icon="Loader" size={OdinIconSize.Medium} spin />
        </div>
      )}
      <div className={containerStyles} ref={tagCreator('container')}>
        {renderHeader()}
        <div className={contentStyles} ref={tagCreator('content')}>
          {children}
        </div>
        {renderFooter()}
      </div>
    </dialog>
  );

  return ReactDOM.createPortal(createDialog(), document.body);
}

export default forwardRef(Dialog);
